import { Card, CardContent, CardHeader, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { CustomerDisplay } from "components/job/details/jobDetailTab/CustomerDisplay";
import { DetailsTimeDisplay } from "components/job/details/jobDetailTab/DetailsTimeDisplay";
import { StyledChips } from "components/StyledChips";
import StyledTextFieldReadOnly from "components/StyledTextFieldReadOnly";
import { intlFormatDate } from "helpers";
import { TabProps } from "models/TabProps";
import { MachineDisplay } from "pages/partner/jobDetails/components/MachineDisplay";
import { Contacts } from "pages/partner/jobDetails/tabs/details/Contacts";
import { FC } from "react";
import { useIntl } from "react-intl";
import { useAppSelector } from "store";
import { selectPageSelectedJob } from "store/root.store";
import { PartnerJobDetailsActions } from "../PartnerJobDetailsActions";

const PREFIX = "JobDetailTab";

const classes = {
  symptomDescription: `${PREFIX}-symptomDescription`,
  bold: `${PREFIX}-bold`,
  historyIconAdjust: `${PREFIX}-eq-history-icon-adjust`,
  iconAutoMargin: `${PREFIX}-icon-auto-margin`,
  iconInfo: `${PREFIX}-icon-info`,
  noPadding: `${PREFIX}-no-padding`,
  leftIndent: `${PREFIX}-left-indent`,
  disabled: `${PREFIX}-disabled`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`& .${classes.bold}`]: {
    fontWeight: 700,
  },
  [`& .${classes.historyIconAdjust}`]: {
    color: theme.palette.info.main,
    position: "relative",
    bottom: "0.1rem",
    marginRight: "0.2rem",
  },
  [`& .${classes.iconAutoMargin}`]: {
    margin: "auto",
  },
  [`& .${classes.iconInfo}`]: {
    color: theme.palette.info.main,
  },
  [`& .${classes.noPadding}`]: {
    paddingTop: "0px",
    paddingBottom: "0px",
  },
  [`& .${classes.leftIndent}`]: {
    marginLeft: "38px",
  },
  [`& .${classes.disabled}`]: {
    color: theme.palette.secondary.main,
  },
}));

export const JobDetailTab: FC<TabProps> = (props: TabProps) => {
  const { setFab } = props;
  const intl = useIntl();
  const job = useAppSelector(selectPageSelectedJob);
  const contacts = job.customer?.contacts || [];

  return (
    <>
      <StyledCard elevation={1} data-testid="JobDetailTab">
        <CardHeader
          sx={{
            fontSize: "1.2rem",
          }}
          title={
            <>
              <DetailsTimeDisplay readonly={true} />
              <CustomerDisplay noHistory />
            </>
          }
          subheader={<MachineDisplay />}
        />
        <CardContent style={{ paddingTop: 0 }}>
          <Grid container direction="column" spacing={2}>
            <Grid item className={classes.leftIndent}>
              {!!job.contract?.code && (
                <Typography>
                  {`${intl.formatMessage({
                    id: "job.contract.code",
                  })}: ${job.contract.code}`}
                </Typography>
              )}
              {!!job.contract?.description && (
                <Typography>
                  {`${intl.formatMessage({
                    id: "job.contract.description",
                  })}: ${job.contract.description}`}
                </Typography>
              )}
              {!!job.contract?.validTo && (
                <Typography>
                  {`${intl.formatMessage({
                    id: "job.contract.validTo",
                  })}: ${intlFormatDate(intl, job.contract.validTo)}`}
                </Typography>
              )}
            </Grid>
            <Grid item sx={{ pt: "4px !important" }}>
              <Contacts
                customerAddress={job.customer?.address!}
                contacts={contacts}
                preferredContact={job.preferredContact}
              />
            </Grid>
            <Grid item>
              <StyledChips
                isOnStop={job.customer?.isOnStop}
                priority={job.priority}
                symptoms={job.symptoms}
              />
            </Grid>
            {job.symptomDescription && (
              <Grid item>
                <StyledTextFieldReadOnly
                  label={intl.formatMessage({ id: "job.symptomDescription" })}
                  multiline
                  minRows={15}
                  defaultValue={job?.symptomDescription}
                  disabled
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </StyledCard>
      <PartnerJobDetailsActions setFab={setFab} />
    </>
  );
};
